import React, { useEffect, useRef, useState, useCallback } from 'react';
import Hls from 'hls.js';
import { castToTv, preloadCast } from '../../utils/castSdk';
import { isTV } from '../../lib/tv';
import './BunnyPlayer.css';

// Host de streaming (HLS) da biblioteca do Bunny Stream. O vídeo é entregue direto
// pelos arquivos .m3u8 (adaptativo) num <video> nativo — SEM o iframe do player do Bunny.
// Assim o WebView do app nunca "navega" para fora e o vídeo toca embutido, e ganhamos
// controle TOTAL do visual (o iframe fechado do Bunny não deixava customizar nada).
const BUNNY_STREAM_HOST = process.env.REACT_APP_BUNNY_STREAM_HOST || 'vz-3ce73773-bf5.b-cdn.net';

// Converte a URL de embed do Bunny (…mediadelivery.net/embed/{lib}/{guid}) na URL do
// HLS playlist. Retorna null se não for um vídeo do Bunny (aí o player não se aplica).
export const getBunnyHlsUrl = (url) => {
    if (!url) return null;
    const match = url.match(/mediadelivery\.net\/embed\/\d+\/([0-9a-fA-F-]{36})/);
    if (!match) return null;
    return `https://${BUNNY_STREAM_HOST}/${match[1]}/playlist.m3u8`;
};

// Ícone-play da logo Reino (usado no botão central). SVG embutido em base64 → self-contained.
const LOGO_PLAY = 'data:image/svg+xml;base64,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';

const fmt = (s) => {
    if (!isFinite(s) || s < 0) s = 0;
    const m = Math.floor(s / 60);
    let ss = Math.floor(s % 60);
    if (ss < 10) ss = '0' + ss;
    return m + ':' + ss;
};

// Detector de presença: após este tanto de vídeo ASSISTIDO sem NENHUMA interação do
// usuário, a troca de vídeo no auto-play para e pergunta "Continuar assistindo?" (evita
// ficar gastando banda tocando pra ninguém). Qualquer interação zera a contagem.
const CONTINUE_AFTER_SEC = 20 * 60; // 20 min assistido sem interação → "Continuar assistindo?"
// Regra de ONDE pausa: vídeo MAIS CURTO que isto → pausa na hora (no meio); MAIS LONGO
// → não interrompe, espera terminar e mostra o popup antes de avançar.
const LONG_VIDEO_SEC = 20 * 60; // 20 minutos

/**
 * Player customizado da Reino Play para vídeos do Bunny (HLS), com a identidade da marca:
 * capa/título/badges, botão central com a logo, e controles próprios (progresso, play,
 * próximo episódio, volume, cast e tela cheia). Ligado a um <video> nativo real.
 */
const BunnyPlayer = ({ url, poster, title, subtitle, badges = [], hasNext = false, onNext, onEnded }) => {
    const playerRef = useRef(null);
    const videoRef = useRef(null);
    const trackRef = useRef(null);
    const volTrackRef = useRef(null);
    const hlsRef = useRef(null);
    const onEndedRef = useRef(onEnded);
    onEndedRef.current = onEnded;
    const hasNextRef = useRef(hasNext);
    hasNextRef.current = hasNext;

    const [playing, setPlaying] = useState(false);
    const [current, setCurrent] = useState(0);
    const [duration, setDuration] = useState(0);
    const [buffered, setBuffered] = useState(0);
    const [volume, setVolume] = useState(1);
    const [muted, setMuted] = useState(false);
    const [isFs, setIsFs] = useState(false);
    const [canCast, setCanCast] = useState(false);
    const [levels, setLevels] = useState([]);        // qualidades disponíveis (hls.js)
    const [selectedLevel, setSelectedLevel] = useState(-1); // -1 = Automático
    const [showSettings, setShowSettings] = useState(false);
    const [isPortrait, setIsPortrait] = useState(false); // vídeo vertical → caixa alta
    const [ratio, setRatio] = useState(null); // proporção REAL do vídeo, ex.: "350 / 640"
    const [askContinue, setAskContinue] = useState(false); // popup "Continuar assistindo?"
    const askContinueRef = useRef(false);
    askContinueRef.current = askContinue;
    const watchedRef = useRef(0); // segundos ASSISTIDOS sem interação (detector de presença)
    const waitEndRef = useRef(false); // vídeo longo: aguardando terminar p/ mostrar o popup
    const endedPauseRef = useRef(false); // popup surgiu no FIM (continuar = avançar) ou no meio (= retomar)

    // ---- HLS: liga o <video> ao playlist .m3u8 do Bunny ----
    useEffect(() => {
        const video = videoRef.current;
        const hlsUrl = getBunnyHlsUrl(url);
        if (!video || !hlsUrl) return undefined;

        setLevels([]);
        setSelectedLevel(-1);

        let hls;
        if (Hls.isSupported()) {
            // Desktop / Android: hls.js — permite escolher a qualidade (menu da engrenagem)
            hls = new Hls({ enableWorker: true });
            hlsRef.current = hls;
            // listener ANTES do loadSource (evita perder o evento)
            hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
                const lv = (data && data.levels) || hls.levels || [];
                setLevels(lv.map((l, i) => ({ index: i, height: l.height })).filter((l) => l.height));
            });
            hls.loadSource(hlsUrl);
            hls.attachMedia(video);
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            // iOS / Safari: HLS nativo (qualidade adapta sozinha, sem menu)
            video.src = hlsUrl;
        } else {
            video.src = hlsUrl;
        }

        const playPromise = video.play?.();
        if (playPromise && playPromise.catch) playPromise.catch(() => { /* autoplay pode exigir toque */ });

        return () => {
            if (hls) { hls.destroy(); hlsRef.current = null; }
        };
    }, [url]);

    // ---- Sincroniza o estado do React com os eventos do <video> ----
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return undefined;

        const onPlay = () => setPlaying(true);
        const onPause = () => setPlaying(false);
        const onTime = () => setCurrent(video.currentTime || 0);
        const onDur = () => setDuration(video.duration || 0);
        const onProgress = () => {
            try {
                if (video.buffered.length && video.duration) {
                    setBuffered(video.buffered.end(video.buffered.length - 1) / video.duration);
                }
            } catch (e) { /* noop */ }
        };
        const onVol = () => { setVolume(video.volume); setMuted(video.muted); };
        // Detecta a orientação real do vídeo (dimensões intrínsecas). Vertical (9:16) → o
        // player vira uma caixa alta em vez de deixar o vídeo minúsculo com tarjas laterais.
        const onMeta = () => {
            if (video.videoWidth && video.videoHeight) {
                setIsPortrait(video.videoHeight > video.videoWidth);
                // proporção real → a caixa se dimensiona pelo vídeo (sem tarja lateral,
                // e a barra de progresso encosta exatamente nas bordas do vídeo).
                setRatio(`${video.videoWidth} / ${video.videoHeight}`);
            }
        };
        // Como o player NÃO é mais destruído entre vídeos (o <video> é reutilizado, só troca
        // a fonte), não há superfície órfã pra congelar — então dá pra CONTINUAR em tela cheia
        // no próximo episódio, sem sair.
        const onEnd = () => {
            // Vídeo LONGO (>= 20 min) que ficou 20 min sem interação: não cortamos no meio —
            // esperamos ele terminar e mostramos o popup aqui, antes de avançar pro próximo.
            if (waitEndRef.current && hasNextRef.current) {
                waitEndRef.current = false;
                endedPauseRef.current = true;
                setAskContinue(true);
                return;
            }
            if (onEndedRef.current) onEndedRef.current();
        };

        video.addEventListener('play', onPlay);
        video.addEventListener('pause', onPause);
        video.addEventListener('timeupdate', onTime);
        video.addEventListener('durationchange', onDur);
        video.addEventListener('progress', onProgress);
        video.addEventListener('volumechange', onVol);
        video.addEventListener('ended', onEnd);
        video.addEventListener('loadedmetadata', onMeta);
        video.addEventListener('resize', onMeta);

        // "Transmitir para a TV": mostra o botão sempre que a plataforma tem a API de cast
        // (AirPlay no iOS / Remote Playback no Chrome). Ao clicar, abre o seletor de dispositivos.
        setCanCast(
            (typeof video.remote !== 'undefined') ||
            (typeof video.webkitShowPlaybackTargetPicker === 'function')
        );

        return () => {
            video.removeEventListener('play', onPlay);
            video.removeEventListener('pause', onPause);
            video.removeEventListener('timeupdate', onTime);
            video.removeEventListener('durationchange', onDur);
            video.removeEventListener('progress', onProgress);
            video.removeEventListener('volumechange', onVol);
            video.removeEventListener('ended', onEnd);
            video.removeEventListener('loadedmetadata', onMeta);
            video.removeEventListener('resize', onMeta);
        };
    }, []);

    // ---- Detector de presença ("Continuar assistindo?") ----
    // Conta os segundos ASSISTIDOS (só enquanto o vídeo TOCA). Qualquer interação do
    // usuário em QUALQUER lugar do app (clique/toque/tecla) ZERA a contagem — é assim que
    // sabemos que tem alguém ali. 20 min sem interação → o onEnd mostra o popup na troca.
    useEffect(() => {
        const tick = setInterval(() => {
            const v = videoRef.current;
            if (!v || v.paused || v.ended || askContinueRef.current) return;
            watchedRef.current += 1;
            if (watchedRef.current >= CONTINUE_AFTER_SEC && !waitEndRef.current) {
                const dur = v.duration || 0;
                if (dur > 0 && dur < LONG_VIDEO_SEC) {
                    // vídeo CURTO: pausa AGORA, onde a pessoa está (áudio normal, só pausa)
                    endedPauseRef.current = false;
                    try { v.pause(); } catch (e) { /* noop */ }
                    setAskContinue(true);
                } else {
                    // vídeo LONGO: não interrompe; mostra o popup quando ele terminar
                    waitEndRef.current = true;
                }
            }
        }, 1000);
        // Qualquer interação do usuário em qualquer lugar zera a contagem (tem gente ali).
        const reset = () => { watchedRef.current = 0; waitEndRef.current = false; };
        document.addEventListener('pointerdown', reset, true);
        document.addEventListener('keydown', reset, true);
        return () => {
            clearInterval(tick);
            document.removeEventListener('pointerdown', reset, true);
            document.removeEventListener('keydown', reset, true);
        };
    }, []);

    const togglePlay = useCallback(() => {
        const video = videoRef.current;
        if (!video) return;
        if (video.paused) { const p = video.play?.(); if (p && p.catch) p.catch(() => {}); }
        else video.pause();
    }, []);

    // ---- Barra de progresso (arrastável) ----
    const seekFromClientX = useCallback((clientX) => {
        const video = videoRef.current;
        const track = trackRef.current;
        if (!video || !track || !video.duration) return;
        const r = track.getBoundingClientRect();
        const frac = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
        video.currentTime = frac * video.duration;
        setCurrent(video.currentTime);
    }, []);
    const seekingRef = useRef(false);
    const onTrackDown = (e) => { seekingRef.current = true; e.currentTarget.setPointerCapture(e.pointerId); seekFromClientX(e.clientX); };
    const onTrackMove = (e) => { if (seekingRef.current) seekFromClientX(e.clientX); };
    const onTrackUp = () => { seekingRef.current = false; };

    // ---- Volume (arrastável) + mudo ----
    const setVolFromClientX = useCallback((clientX) => {
        const video = videoRef.current;
        const track = volTrackRef.current;
        if (!video || !track) return;
        const r = track.getBoundingClientRect();
        const v = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
        video.volume = v;
        video.muted = v === 0;
    }, []);
    const vSeekingRef = useRef(false);
    const onVolDown = (e) => { vSeekingRef.current = true; e.currentTarget.setPointerCapture(e.pointerId); setVolFromClientX(e.clientX); };
    const onVolMove = (e) => { if (vSeekingRef.current) setVolFromClientX(e.clientX); };
    const onVolUp = () => { vSeekingRef.current = false; };
    const toggleMute = () => { const video = videoRef.current; if (video) video.muted = !video.muted; };

    // ---- Tela cheia (nativa + fallback CSS) ----
    const toggleFs = useCallback(() => {
        const el = playerRef.current;
        if (!el) return;
        const goFs = !isFs;
        setIsFs(goFs);
        try {
            if (goFs && el.requestFullscreen) { const p = el.requestFullscreen(); if (p && p.catch) p.catch(() => {}); }
            else if (!goFs && document.fullscreenElement && document.exitFullscreen) document.exitFullscreen();
        } catch (e) { /* fullscreen nativo bloqueado → o modo CSS já cobre */ }
    }, [isFs]);

    useEffect(() => {
        const onFsChange = () => { if (!document.fullscreenElement) setIsFs(false); };
        document.addEventListener('fullscreenchange', onFsChange);
        return () => document.removeEventListener('fullscreenchange', onFsChange);
    }, []);

    // ---- Transmitir para a TV (Remote Playback API / AirPlay) ----
    const cast = async () => {
        const video = videoRef.current;
        if (!video) return;
        // iOS / Safari: AirPlay nativo (Apple TV)
        if (typeof video.webkitShowPlaybackTargetPicker === 'function') {
            try { video.webkitShowPlaybackTargetPicker(); return; } catch (e) { /* segue pro Cast */ }
        }
        // Chrome (desktop/Android): Google Cast → Chromecast (manda a URL HLS pra TV)
        const ok = await castToTv(getBunnyHlsUrl(url), { title, poster });
        if (!ok && video.remote && video.remote.prompt) {
            video.remote.prompt().catch(() => {}); // último recurso (Remote Playback)
        }
    };

    // pré-carrega o SDK do Chromecast (deixa o botão pronto no Chrome)
    useEffect(() => { preloadCast(); }, []);

    // ---- Qualidade (hls.js): -1 = automático, ou o índice do nível ----
    const setQuality = (levelIndex) => {
        const hls = hlsRef.current;
        if (hls) hls.currentLevel = levelIndex;
        setSelectedLevel(levelIndex);
        setShowSettings(false);
    };

    // fecha o menu de qualidade ao clicar fora dele
    useEffect(() => {
        if (!showSettings) return undefined;
        const onDown = (e) => { if (!e.target.closest('.rp-settings')) setShowSettings(false); };
        document.addEventListener('pointerdown', onDown);
        return () => document.removeEventListener('pointerdown', onDown);
    }, [showSettings]);

    // ---- Teclado: espaço = play/pause, Esc = sai da tela cheia ----
    useEffect(() => {
        const onKey = (e) => {
            const tag = document.activeElement && document.activeElement.tagName;
            if (e.code === 'Space' && !/INPUT|TEXTAREA/.test(tag || '')) { e.preventDefault(); togglePlay(); }
            if (e.key === 'Escape' && isFs) setIsFs(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [togglePlay, isFs]);

    // ---- CONTROLE REMOTO (só na TV): setas controlam o VÍDEO, não a navegação ----
    // Na página do player o controle mexe no vídeo: ←/→ pulam 10s, ↑/↓ volume, OK play/pause.
    // Capture:true + stopPropagation pra AGIR ANTES do TvSpatialNav (que fica na janela) e
    // impedir que ele trate a mesma tecla. Fora da TV este efeito nem liga (isTV=false).
    useEffect(() => {
        if (!isTV) return undefined;
        const onKey = (e) => {
            const tag = document.activeElement && document.activeElement.tagName;
            if (/INPUT|TEXTAREA|SELECT/.test(tag || '')) return;
            const v = videoRef.current;
            if (!v) return;
            const stop = () => { e.preventDefault(); e.stopPropagation(); };
            switch (e.key) {
                case 'ArrowRight': stop(); v.currentTime = Math.min(v.duration || 0, (v.currentTime || 0) + 10); break;
                case 'ArrowLeft':  stop(); v.currentTime = Math.max(0, (v.currentTime || 0) - 10); break;
                case 'ArrowUp':    stop(); v.muted = false; v.volume = Math.min(1, (v.volume || 0) + 0.1); break;
                case 'ArrowDown':  stop(); v.volume = Math.max(0, (v.volume || 0) - 0.1); break;
                case 'Enter':      stop(); togglePlay(); break;
                default: break;
            }
        };
        document.addEventListener('keydown', onKey, true);
        return () => document.removeEventListener('keydown', onKey, true);
    }, [togglePlay]);

    const onScreenClick = (e) => {
        if (e.target.closest('.rp-controls') || e.target.closest('.rp-top-meta') || e.target.closest('.rp-big-play')) return;
        togglePlay();
    };

    // Clique em "Continuar assistindo": zera a contagem e retoma. Se o popup surgiu no
    // MEIO (vídeo curto), retoma o mesmo vídeo de onde parou; se surgiu no FIM (vídeo
    // longo), avança pro próximo.
    const continueWatching = () => {
        watchedRef.current = 0;
        setAskContinue(false);
        const v = videoRef.current;
        if (endedPauseRef.current) {
            endedPauseRef.current = false;
            if (onEndedRef.current) onEndedRef.current();
        } else if (v) {
            const p = v.play?.(); if (p && p.catch) p.catch(() => {});
        }
    };

    const volDisplay = muted ? 0 : volume;

    return (
        <div
            className={`rp-player${playing ? ' is-playing' : ''}${isFs ? ' rp-fs' : ''}${isPortrait ? ' rp-portrait' : ''}`}
            style={isPortrait && ratio && !isFs ? { aspectRatio: ratio } : undefined}
            ref={playerRef}
        >
            <video
                ref={videoRef}
                className="rp-screen"
                playsInline
                poster={poster || undefined}
                onClick={onScreenClick}
                x-webkit-airplay="allow"
            />

            {askContinue && (
                <div className="rp-continue" role="dialog" aria-label="Continuar assistindo">
                    <div className="rp-continue-card">
                        <h2>Continuar assistindo?</h2>
                        <p>Você ainda está aí?</p>
                        <button className="rp-continue-btn" onClick={continueWatching}>
                            Continuar assistindo
                        </button>
                    </div>
                </div>
            )}

            <div className="rp-top-meta">
                <div className="rp-meta-text">
                    <h1>{title}</h1>
                    {subtitle ? <p className="rp-sub">{subtitle}</p> : null}
                </div>
                {badges.length > 0 && (
                    <div className="rp-badges">
                        {badges.map((b, i) => (
                            <span key={i} className={`rp-badge rp-badge-${b.kind || 'q'}`}>{b.label}</span>
                        ))}
                    </div>
                )}
            </div>

            <button className="rp-big-play" onClick={togglePlay} aria-label="Reproduzir episódio">
                <img src={LOGO_PLAY} alt="" />
            </button>

            <div className="rp-controls">
                <div
                    className="rp-track"
                    ref={trackRef}
                    onPointerDown={onTrackDown}
                    onPointerMove={onTrackMove}
                    onPointerUp={onTrackUp}
                >
                    <div className="rp-buffered" style={{ width: `${buffered * 100}%` }} />
                    <div className="rp-fill" style={{ width: `${duration ? (current / duration) * 100 : 0}%` }}>
                        <span className="rp-knob" />
                    </div>
                </div>

                <div className="rp-row">
                    <button className="rp-ctl rp-play" onClick={togglePlay} aria-label={playing ? 'Pausar' : 'Reproduzir'}>
                        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            {playing
                                ? <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
                                : <path d="M8 5v14l11-7z" />}
                        </svg>
                    </button>

                    <button className="rp-ctl" onClick={onNext} disabled={!hasNext} aria-label="Próximo episódio">
                        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" /></svg>
                    </button>

                    <span className="rp-time"><span>{fmt(current)}</span> / <span>{fmt(duration)}</span></span>
                    <span className="rp-spacer" />

                    <div className="rp-vol">
                        <button className="rp-ctl" onClick={toggleMute} aria-label={volDisplay === 0 ? 'Ativar som' : 'Silenciar'}>
                            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                {volDisplay === 0
                                    ? <><path d="M3 9v6h4l5 5V4L7 9H3z" /><line x1="15" y1="8" x2="21" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></>
                                    : <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3a4.5 4.5 0 00-2.5-4v8a4.5 4.5 0 002.5-4z" />}
                            </svg>
                        </button>
                        <div className="rp-track rp-voltrack" ref={volTrackRef} onPointerDown={onVolDown} onPointerMove={onVolMove} onPointerUp={onVolUp}>
                            <div className="rp-fill" style={{ width: `${volDisplay * 100}%` }}><span className="rp-knob" /></div>
                        </div>
                    </div>

                    {levels.length > 1 && (
                        <div className="rp-settings">
                            <button className="rp-ctl" onClick={() => setShowSettings((s) => !s)} aria-label="Qualidade do vídeo" aria-expanded={showSettings}>
                                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19.14 12.94a7.5 7.5 0 000-1.88l2.03-1.58a.5.5 0 00.12-.64l-1.92-3.32a.5.5 0 00-.6-.22l-2.39.96a7.3 7.3 0 00-1.62-.94l-.36-2.54a.5.5 0 00-.5-.42h-3.84a.5.5 0 00-.5.42l-.36 2.54c-.58.24-1.12.56-1.62.94l-2.39-.96a.5.5 0 00-.6.22L2.7 8.84a.5.5 0 00.12.64l2.03 1.58a7.5 7.5 0 000 1.88l-2.03 1.58a.5.5 0 00-.12.64l1.92 3.32c.14.24.42.32.66.22l2.39-.96c.5.38 1.04.7 1.62.94l.36 2.54c.05.24.26.42.5.42h3.84c.24 0 .45-.18.5-.42l.36-2.54c.58-.24 1.12-.56 1.62-.94l2.39.96c.24.1.52.02.66-.22l1.92-3.32a.5.5 0 00-.12-.64l-2.03-1.58zM12 15.5a3.5 3.5 0 110-7 3.5 3.5 0 010 7z" /></svg>
                            </button>
                            {showSettings && (
                                <div className="rp-menu" role="menu">
                                    <button
                                        className={`rp-menu-item${selectedLevel === -1 ? ' rp-menu-on' : ''}`}
                                        onClick={() => setQuality(-1)}
                                        role="menuitemradio"
                                        aria-checked={selectedLevel === -1}
                                    >
                                        Automático
                                    </button>
                                    {[...levels].sort((a, b) => b.height - a.height).map((l) => (
                                        <button
                                            key={l.index}
                                            className={`rp-menu-item${selectedLevel === l.index ? ' rp-menu-on' : ''}`}
                                            onClick={() => setQuality(l.index)}
                                            role="menuitemradio"
                                            aria-checked={selectedLevel === l.index}
                                        >
                                            {l.height}p
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {canCast && (
                        <button className="rp-ctl" onClick={cast} aria-label="Transmitir para a TV">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M2 8V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-5" /><path d="M2 12a6 6 0 0 1 6 6" /><path d="M2 16a2 2 0 0 1 2 2" /><path d="M2 20h.01" />
                            </svg>
                        </button>
                    )}

                    <button className="rp-ctl" onClick={toggleFs} aria-label={isFs ? 'Sair da tela cheia' : 'Tela cheia'}>
                        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 7h3V5H5v5h2V7zm10 0v3h2V5h-5v2h3zM7 17v-3H5v5h5v-2H7zm10 0h-3v2h5v-5h-2v3z" /></svg>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BunnyPlayer;
